import { SyntheticEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import lpn from "google-libphonenumber";
import Container from "@mui/material/Container";
import {
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { TCreateOneTimeTokenRequest, createOneTimeToken } from "../../api/auth";
import { AxiosError } from "axios";
import {
  ERROR_MESSAGE_GENERIC,
  ERROR_MESSAGE_USER_NOT_FOUND,
} from "../../constants";
import { ContainedButton } from "../styled/contained-button";

// Get an instance of `PhoneNumberUtil`.
const phoneUtil = lpn.PhoneNumberUtil.getInstance();

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export default function SignIn() {
  const [signInOption, setSignInOption] = useState<"email" | "phone">("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const getToken = (request: TCreateOneTimeTokenRequest) => {
    setLoading(true);

    createOneTimeToken(request)
      .then(() => {
        navigate(`/sign-in/${signInOption}`);
      })
      .catch((error: AxiosError) => {
        if (error.response?.status === 404) {
          setError(ERROR_MESSAGE_USER_NOT_FOUND);
        } else {
          setError(ERROR_MESSAGE_GENERIC);
        }
      })
      .finally(() => setLoading(false));
  };

  const submitEmail = () => {
    const formattedEmail = email?.trim();
    if (!formattedEmail) {
      setError("Bitte geben Sie eine Email ein");
      return;
    }

    if (!EMAIL_REGEX.test(formattedEmail)) {
      setError("Bitte geben Sie eine gültige Email ein");
      return;
    }

    getToken({ email: formattedEmail });
  };

  const submitPhone = () => {
    const formattedPhone = phone?.trim();
    if (!formattedPhone) {
      setError("Bitte geben Sie eine Telefonnummer ein");
      return;
    }

    try {
      const number = phoneUtil.parse(formattedPhone);
      getToken({
        phone: `+${number.getCountryCode()}${number.getNationalNumber()}`,
      });
    } catch (e) {
      try {
        const number = phoneUtil.parse(formattedPhone, "DE");
        getToken({
          phone: `+${number.getCountryCode()}${number.getNationalNumber()}`,
        });
      } catch (e) {
        setError("Bitte geben Sie eine gültige Telefonnummer ein");
      }
    }
  };

  const handleSubmit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    signInOption === "email" ? submitEmail() : submitPhone();
  };

  return (
    <Container component="main">
      <Grid
        container
        flexDirection="column"
        spacing={3}
        mt={3}
        sx={{ margin: "auto", width: { xs: "90%", md: "60%" } }}
      >
        <Grid item xs={11} md={12}>
          <Typography variant="h4">
            Wir senden Ihnen den Link zum Anmelden.
          </Typography>
        </Grid>
        <Grid item xs={11} md={12}>
          <Typography variant="h6">
            Bitte wählen Sie aus, wie Sie den Link erhalten möchten: Per E-Mail
            oder per SMS.
          </Typography>
        </Grid>
        <Grid item xs={11} md={12}>
          <Typography variant="h6">
            Bitte tragen Sie dann Ihre E-Mail-Adresse oder Handynummer ein.
          </Typography>
        </Grid>
        <Grid item xs={11} md={12}>
          <Typography variant="h6">
            Klicken Sie anschließend auf den Link in der E- Mail oder in der SMS
            und Sie werden automatisch angemeldet.
          </Typography>
        </Grid>
        <Grid item xs={11} md={12} width="100%">
          <FormControl sx={{ alignItems: "center", width: "100%" }}>
            <RadioGroup
              row
              aria-label="login option"
              value={signInOption}
              onChange={(event) =>
                setSignInOption(event.target.value as "email" | "phone")
              }
            >
              <FormControlLabel
                value="email"
                control={<Radio />}
                label="E-Mail"
              />
              <FormControlLabel
                value="phone"
                control={<Radio />}
                label="SMS (Handynummer)"
              />
            </RadioGroup>
          </FormControl>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            {signInOption === "email" && (
              <TextField
                type="email"
                margin="normal"
                required
                fullWidth
                id="email"
                label="E-Mail-Adresse"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => {
                  setError("");
                  setEmail(e.target.value);
                }}
                placeholder="mustermann@gmail.com"
                helperText={error}
                error={!!error}
              />
            )}

            {signInOption === "phone" && (
              <TextField
                type="tel"
                margin="normal"
                required
                fullWidth
                id="phone"
                label="Telefonnummer"
                name="phone"
                autoComplete="phone"
                autoFocus
                value={phone}
                onChange={(e) => {
                  setError("");
                  setPhone(e.target.value);
                }}
                helperText={error}
                error={!!error}
              />
            )}
            <ContainedButton
              muiType="loading"
              type="submit"
              fullWidth
              variant="contained"
              loading={loading}
              muiStyleOverride={{ mt: 3, mb: 2 }}
            >
              Weiter
            </ContainedButton>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}
